import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  VENUE_AUDIT_KIND_HINT,
  summariseFinding,
  venueAuditKindLabel,
} from "@/lib/tina/venue-audit";
import type { VenueAuditFindingRow, VenueAuditKind } from "@/types/tina";

interface DismissFindingDialogProps {
  /** The finding to dismiss; `null` closes the dialog. */
  finding: VenueAuditFindingRow | null;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (note: string) => void;
}

/**
 * The one confirmation on this page, because a dismissal is permanent.
 *
 * It re-states the finding rather than asking "are you sure?" about an id, and it says out
 * loud what dismissal means: the row is gone until the audit sees a materially different
 * version of it — a venue joining or leaving the set, or the severity rising. A changed hit
 * count, a new cache row on the same venue or a reworded venue name will NOT bring it back,
 * by design, or the 35 same-building collisions would return every morning.
 *
 * The note is optional and free text. It is the only record of why a curator decided a
 * finding was acceptable, and the next person to look at this venue reads it.
 */
export function DismissFindingDialog({
  finding,
  pending,
  onOpenChange,
  onConfirm,
}: DismissFindingDialogProps) {
  const [note, setNote] = useState("");

  // A fresh note per finding: carrying the previous one over would attribute one
  // curator's reasoning to a different venue.
  useEffect(() => {
    setNote("");
  }, [finding?.id]);

  const hint = finding
    ? VENUE_AUDIT_KIND_HINT[finding.kind as VenueAuditKind]
    : undefined;

  return (
    <Dialog open={finding !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dismiss this finding?</DialogTitle>
          <DialogDescription>
            It will not appear again unless the venues involved change, or the
            finding gets worse. There is no undo on this page.
          </DialogDescription>
        </DialogHeader>

        {finding ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  finding.severity === "high" ? "destructive" : "secondary"
                }
                className="text-[10px] uppercase"
              >
                {finding.severity}
              </Badge>
              <Badge variant="outline" className="text-[10px] font-normal">
                {venueAuditKindLabel(finding.kind)}
              </Badge>
              <span className="font-mono text-[10px] text-muted-foreground">
                {finding.fingerprint}
              </span>
            </div>
            <p className="text-sm">{summariseFinding(finding)}</p>
            {hint ? (
              <p className="text-xs text-muted-foreground">{hint}</p>
            ) : null}
            {finding.evidence.name_variants?.length ? (
              <p className="text-xs text-muted-foreground">
                Spellings: {finding.evidence.name_variants.join(" · ")}
              </p>
            ) : null}
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Why is this acceptable? Optional, but the next curator reads it."
              className="min-h-[80px] text-sm"
            />
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Keep it
          </Button>
          <Button onClick={() => onConfirm(note)} disabled={pending}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Dismiss for good
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
